import { inject, Injectable, NgZone } from "@angular/core";
import { fetchEventSource } from "src/app/sse/vendor/fetch-event-source";
import { DxDataGridComponent } from "devextreme-angular";
import { isArray } from "es-toolkit/compat";
import { sift } from "radash";
import { BehaviorSubject, Subject } from "rxjs";
import { sntLog } from "src/app/common/logger";
import type {
  BrokerDispatchedEvent,
  BrokerEventEnvelope,
  BrokerEventListener,
  BrokerMessage,
  BrokerRawMessage
} from "src/app/sse/broker-event.types";
import { SseEvents } from "src/app/sse/sse-events";
import { nextRuntimeId } from "src/app/support/runtime-id";
import { reportError } from "src/app/support/error-reporting";
import {
  BrokerResolverService,
  isIgnorableResolverHttpError
} from "src/app/sse/broker-resolver.service";
import { TabServiceTab } from "src/app/services/tab.service";

export type {
  BrokerDispatchedEvent,
  BrokerRawMessage
} from "src/app/sse/broker-event.types";

export type BrokerStatus = "connecting" | "connected" | "not-connected";

const BROKER_RECENT_SSE_EVENT_BUFFER_SIZE = 1000;

const logger = sntLog("BrokerService");

class SSEAuthExpiredError extends Error {
  constructor() {
    super("SSEAuthExpiredError");
  }
}
class SSERetriableError extends Error {
  constructor() {
    super("SSERetriableError");
  }
}

@Injectable()
export class BrokerService {
  static instance: BrokerService | undefined;

  status$: BehaviorSubject<BrokerStatus> = new BehaviorSubject<BrokerStatus>(
    "not-connected"
  );

  message$: Subject<void> = new Subject();

  readonly #listernesMap = new Map<
    SseEvents,
    Array<{
      id: string;
      listener: BrokerEventListener;
      tab: TabServiceTab | undefined;
      resourcesFactory?: () => Promise<string[] | DxDataGridComponent>;
    }>
  >();

  #abortController: AbortController | undefined;

  readonly #recentSseMessages: BrokerMessage[] = [];

  readonly #brokerLoadersService = inject(BrokerResolverService);
  readonly #ngZone = inject(NgZone);

  constructor() {
    BrokerService.instance = this;
    this.#brokerLoadersService.registerLoaders();
  }

  async start({
    headersFactory,
    endpointFactory
  }: {
    headersFactory: () => Promise<Record<string, string>>;
    endpointFactory: () => Promise<string>;
  }): Promise<void> {
    const headers: Record<string, string> = await headersFactory();
    const endpoint: string = await endpointFactory();

    this.#abortController = new AbortController();

    await new Promise<void>((res, rej) => {
      fetchEventSource(endpoint, {
        headers,
        signal: this.#abortController?.signal,
        openWhenHidden: false,
        onopen: async (response) => {
          logger.debug("onopen");

          switch (true) {
            case response.ok &&
              response.headers.get("content-type") === "text/event-stream": {
              logger.debug("onopen success");
              this.status$.next("connected");

              res();
              return; // everything's good
            }
            case response.status === 401: {
              logger.error("onopen unauthorized");
              // if the server closes the connection unexpectedly, retry:
              throw new SSEAuthExpiredError();
            }
            default: {
              logger.warn("onopen retry");
              throw new SSERetriableError();
            }
          }
        },
        onmessage: (msg) => {
          this.#onMessage(msg as BrokerRawMessage);
        },
        onclose: () => {
          this.status$.next("not-connected");
          logger.debug("onclose");

          throw new SSERetriableError();
        },
        onabort: () => {
          this.status$.next("not-connected");
          logger.debug("onabort");
        },
        onerror: (err) => {
          logger.warn(`onerror ${String(err)}`);

          this.status$.next("connecting");

          if (err instanceof SSEAuthExpiredError) {
            throw err;
          }

          return 2000;
        }
      }).catch((error) => {
        if (error instanceof SSEAuthExpiredError) {
          this.status$.next("connecting");

          return this.start({
            headersFactory,
            endpointFactory
          });
        } else {
          rej(error);
          throw error;
        }
      });
    });
  }

  async stop(): Promise<void> {
    this.#abortController?.abort();
    this.status$.next("not-connected");
  }

  addListener({
    type,
    listener,
    resourcesFactory,
    tab
  }: {
    type: SseEvents;
    listener: BrokerEventListener;
    tab: TabServiceTab | undefined;
    resourcesFactory?: () => Promise<string[] | DxDataGridComponent>;
  }): string {
    const id = nextRuntimeId();

    this.#listernesMap.set(type, [
      ...(this.#listernesMap.get(type) || []),
      { id, listener, resourcesFactory, tab }
    ]);

    return id;
  }

  removeListener({ id, type }: { id: string; type: SseEvents }): void {
    this.#listernesMap.set(type, [
      ...(this.#listernesMap.get(type) || []).filter((x) => x.id !== id)
    ]);
  }

  publish(msg: { event: SseEvents; data?: any; isOptmistic: boolean }): void {
    logger.debug("publish", msg);
    this.message$.next();

    if (this.#listernesMap.has(msg.event)) {
      this.#listernesMap
        .get(msg.event)
        ?.forEach(({ listener }) =>
          listener(getMessagePayloadWithoutEventProperty(msg))
        );
    }
  }

  #onMessage(rawMessage: BrokerRawMessage): void {
    void this.#dispatchMessage(rawMessage).catch((error: unknown) => {
      logger.error("dispatchMessage failed", error);

      if (!isIgnorableResolverHttpError(error)) {
        reportError(error);
      }
    });
  }

  async #dispatchMessage(rawMessage: BrokerRawMessage): Promise<void> {
    const message: BrokerMessage = this.#rawToMessage(rawMessage);

    logger.debug("dispatch type", message.event.type);
    logger.debug("dispatched message", JSON.stringify(message));

    if (!message.event.type) {
      logger.warn("No event type found in message", message);
      return;
    }

    if (this.#isDuplicateSseEventById(message)) {
      return;
    }

    this.#recordRecentSseMessage(message);

    const envelope: BrokerEventEnvelope = {
      id: message.id,
      event: message.event
    };

    const listenerEntry = this.#listernesMap.get(message.event.type);

    if (!listenerEntry) {
      logger.warn("No listener entry found for message", message);
      return;
    }

    const designatedListeners = sift(
      await Promise.all(
        listenerEntry.map(async ({ resourcesFactory, listener, tab }) => {
          const resourcesOrDataGrid: string[] | DxDataGridComponent =
            resourcesFactory ? await resourcesFactory() : [];

          let resources: string[] = [];

          if (isArray(resourcesOrDataGrid)) {
            resources = resourcesOrDataGrid;
          } else {
            resources = (<DxDataGridComponent>resourcesOrDataGrid).instance
              .getVisibleRows()
              .map((x) => x.key);
          }

          if (
            resources.length === 0 ||
            resources.includes(message.event.resource)
          ) {
            return {
              listener
            };
          }

          return null;
        })
      )
    ).map(({ listener }) => listener);

    if (!designatedListeners || designatedListeners.length === 0) {
      logger.warn("No designated listeners found for message", message);
      return;
    }

    const resolved: unknown | undefined =
      await this.#brokerLoadersService.loadFnForEvent({
        event: message.event.type,
        resource: message.event.resource
      });

    const extended: BrokerDispatchedEvent = {
      ...envelope,
      resolved
    };

    this.message$.next();

    designatedListeners.forEach((listener) => {
      this.#ngZone.run(() => {
        listener(extended);
      });
    });
  }

  #rawToMessage(message: BrokerRawMessage): BrokerMessage {
    const colonIndex: number = message.event.indexOf(":");
    const source: string =
      colonIndex >= 0 ? message.event.slice(0, colonIndex) : message.event;
    const type: SseEvents = message.event as SseEvents;

    return {
      id: message.id,
      event: {
        source,
        type,
        resource: message.data
      }
    };
  }

  #isDuplicateSseEventById(message: BrokerMessage): boolean {
    const trimmedId: string = message.id.trim();

    if (trimmedId.length === 0) {
      return false;
    }

    const isDuplicate: boolean = this.#recentSseMessages.some(
      (queued: BrokerMessage) => queued.id.trim() === trimmedId
    );

    if (isDuplicate) {
      logger.debug("Ignoring duplicate SSE event", {
        id: trimmedId,
        type: message.event.type
      });

      return true;
    }

    return false;
  }

  #recordRecentSseMessage(message: BrokerMessage): void {
    this.#recentSseMessages.push(message);

    while (
      this.#recentSseMessages.length > BROKER_RECENT_SSE_EVENT_BUFFER_SIZE
    ) {
      this.#recentSseMessages.shift();
    }
  }
}

function getMessagePayloadWithoutEventProperty(msg: any): any {
  const { event, ...rest } = msg;
  return {
    ...rest,
    type: event
  };
}
